/**
 * User API Service
 *
 * API methods for user operations including invitations, updates, and deletion.
 * Updated to use standardized API response format.
 */

import apiClient from './client';
import type { ApiSuccessResponse, ApiCollectionResponse, ApiActionResponse } from '../types/api.types';
import type {
  UserSanitized,
  UserInvitationDTO,
  BatchUserInvitationDTO,
  UserAcceptInvitationDTO,
  UserUpdateDTO,
  UserSelfUpdateDTO,
  PasswordUpdateDTO,
  UserListQuery,
  InvitationSanitized,
} from '../types/user.types';

// ============================================================================
// USER SERVICE
// ============================================================================

export const userService = {
  /**
   * List all users for a tenant
   */
  listUsers: async (tenantId: string, query?: UserListQuery): Promise<UserSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<UserSanitized>>(`/tenants/${tenantId}/users`, {
      params: query,
    });
    return response.data.data;
  },

  /**
   * Get user by ID
   */
  getUser: async (tenantId: string, userId: string): Promise<UserSanitized> => {
    const response = await apiClient.get<ApiSuccessResponse<UserSanitized>>(`/tenants/${tenantId}/users/${userId}`);
    return response.data.data;
  },

  /**
   * Invite a single user
   */
  inviteUser: async (tenantId: string, data: UserInvitationDTO): Promise<InvitationSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<InvitationSanitized>>(`/tenants/${tenantId}/users/invite`, data);
    return response.data.data;
  },

  /**
   * Invite multiple users
   */
  inviteUsers: async (tenantId: string, data: BatchUserInvitationDTO): Promise<InvitationSanitized[]> => {
    const response = await apiClient.post<ApiSuccessResponse<InvitationSanitized[]>>(`/tenants/${tenantId}/users/invite/batch`, data);
    return response.data.data;
  },

  /**
   * Accept user invitation
   */
  acceptInvitation: async (tenantId: string, data: UserAcceptInvitationDTO): Promise<UserSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<UserSanitized>>(`/tenants/${tenantId}/users/accept-invitation`, data);
    return response.data.data;
  },

  /**
   * Update user (admin)
   */
  updateUser: async (tenantId: string, userId: string, data: UserUpdateDTO): Promise<UserSanitized> => {
    const response = await apiClient.put<ApiSuccessResponse<UserSanitized>>(`/tenants/${tenantId}/users/${userId}`, data);
    return response.data.data;
  },

  /**
   * Update own profile (self-update)
   */
  updateSelf: async (tenantId: string, userId: string, data: UserSelfUpdateDTO): Promise<UserSanitized> => {
    const response = await apiClient.put<ApiSuccessResponse<UserSanitized>>(`/tenants/${tenantId}/users/${userId}/self`, data);
    return response.data.data;
  },

  /**
   * Update password
   */
  updatePassword: async (tenantId: string, userId: string, data: PasswordUpdateDTO): Promise<{ message: string }> => {
    const response = await apiClient.put<ApiActionResponse>(`/tenants/${tenantId}/users/${userId}/password`, data);
    return { message: response.data.message };
  },

  /**
   * Delete user
   */
  deleteUser: async (tenantId: string, userId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<ApiActionResponse>(`/tenants/${tenantId}/users/${userId}`);
    return { message: response.data.message };
  },

  /**
   * List pending invitations
   */
  listInvitations: async (tenantId: string): Promise<InvitationSanitized[]> => {
    const response = await apiClient.get<ApiCollectionResponse<InvitationSanitized>>(`/tenants/${tenantId}/invitations`);
    return response.data.data;
  },

  /**
   * Cancel invitation
   */
  cancelInvitation: async (tenantId: string, invitationId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<ApiActionResponse>(`/tenants/${tenantId}/invitations/${invitationId}`);
    return { message: response.data.message };
  },

  /**
   * Resend invitation email
   */
  resendInvitation: async (tenantId: string, invitationId: string): Promise<{ message: string }> => {
    const response = await apiClient.post<ApiActionResponse>(`/tenants/${tenantId}/invitations/${invitationId}/resend`);
    return { message: response.data.message };
  },

  /**
   * Add application to user's favorites
   */
  addFavoriteApplication: async (tenantId: string, userId: string, applicationId: string): Promise<UserSanitized> => {
    const response = await apiClient.post<ApiSuccessResponse<UserSanitized>>(`/tenants/${tenantId}/users/${userId}/favorites/${applicationId}`);
    return response.data.data;
  },

  /**
   * Remove application from user's favorites
   */
  removeFavoriteApplication: async (tenantId: string, userId: string, applicationId: string): Promise<UserSanitized> => {
    const response = await apiClient.delete<ApiSuccessResponse<UserSanitized>>(`/tenants/${tenantId}/users/${userId}/favorites/${applicationId}`);
    return response.data.data;
  },
};

export default userService;
